/**
 * 1-based ordinals for the rashis and nakshatras.
 *
 * The MOA study rows that end in a sign or nakshatra dropdown carry a numeric "Add value" that is
 * conventionally that body's position in the zodiac — Libra is the 7th sign, Ashwini the 1st
 * nakshatra. Typing it by hand is both tedious and easy to get wrong by one, so the dropdown fills
 * it in.
 *
 * Held here rather than in the dialog because the ordering is a fact about the zodiac, not about
 * one form, and several dialogs already keep their own copies of these lists.
 */

/** The twelve rashis, in order. Index + 1 is the rashi number. */
export const RASHIS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
];

/** The twenty-seven nakshatras, in order. Index + 1 is the nakshatra number. */
export const NAKSHATRAS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
  'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
  'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha',
  'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati',
];

/**
 * Position of a name in a list, 1-based, or null when it is not in it.
 *
 * Matched case- and whitespace-insensitively: these lists are duplicated across several dialogs
 * and the copies do not agree on spacing in the two-word nakshatra names.
 */
function ordinalIn(list, name) {
  if (typeof name !== 'string') return null;
  const key = name.trim().toLowerCase().replace(/\s+/g, ' ');
  const i = list.findIndex(n => n.toLowerCase() === key);
  return i === -1 ? null : i + 1;
}

/** 1-12 for a rashi, or null. */
export function rashiIndex(name) {
  return ordinalIn(RASHIS, name);
}

/** 1-27 for a nakshatra, or null. */
export function nakshatraIndex(name) {
  return ordinalIn(NAKSHATRAS, name);
}

/**
 * The value to put in an "Add value" box for a selection.
 *
 * Returns a STRING, because the field is a controlled text input: handing it a number would make
 * the input switch between controlled and uncontrolled as the value changed, and React warns and
 * loses the cursor when that happens.
 *
 * An unrecognised name leaves the existing value alone rather than blanking it — better to keep
 * whatever the user had than to clear a field because a list somewhere disagrees about spelling.
 */
export function autoValueFor(kind, name, currentValue) {
  const n = kind === 'nakshatra' ? nakshatraIndex(name) : rashiIndex(name);
  return n === null ? currentValue : String(n);
}
